import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, MapPin } from 'lucide-react';
import { PlotMap, type PlotGeometry } from '@/screens/parts/PlotMap';
import { formatDate, formatNumber } from '@/components/live';
import { callRpc } from '@/api/resources';
import { isSupabaseConfigured } from '@/lib/supabase';

interface Feature {
  geometry: PlotGeometry;
  properties: {
    plot_code: string; plot_name: string | null; farmer_name: string | null;
    version_no: number; is_current: boolean; approval_status: string;
    capture_method: string | null; area_ha: number | null; accuracy_meters: number | null;
    captured_on: string | null;
  };
}

interface Monitored {
  monitoring_id: string;
  plot_code: string;
  watched_version: number;
  watching_current: boolean;
  watched: Feature | null;
  current: Feature | null;
  monitored_area_ha: number | null;
}

/**
 * The polygon satellite monitoring is actually watching (owner, 2026-09-25: "Satellite, need to
 * check the farm polygon?").
 *
 * Monitoring is pinned to the geometry VERSION approved when it was activated, and that is
 * deliberate: a boundary amended later must not silently re-cut the history, or a change on the
 * ground and a change on the map become the same event. The cost is that the shape being watched
 * can quietly stop being the shape the plot now has, and until this panel there was no way to see
 * either fact.
 *
 * So it draws what is being watched, and when the plot has moved on it says so, with both areas,
 * rather than leaving a reader to compare version numbers in their head.
 */
export function MonitoredBoundary({ monitoringId }: { monitoringId: string }) {
  const query = useQuery({
    queryKey: ['monitored-geometry', monitoringId],
    queryFn: () => callRpc<Monitored | null>('fn_monitored_geometry', { p_monitoring_id: monitoringId }),
    enabled: isSupabaseConfigured && Boolean(monitoringId),
  });

  if (query.isLoading) return <p className="muted">Loading the boundary...</p>;
  const data = query.data;
  if (!data?.watched) return <p className="state-note">No boundary is recorded for this plot.</p>;

  const watched = data.watched.properties;
  const current = data.current?.properties;

  return (
    <div className="monitored-boundary">
      {!data.watching_current && current && (
        <p className="state-note is-warn">
          <AlertTriangle size={14} />
          <span>
            The boundary has been amended since monitoring started. The satellite is watching
            version {watched.version_no} ({formatNumber(watched.area_ha, 3)} ha); the plot is now on
            version {current.version_no} ({formatNumber(current.area_ha, 3)} ha). That is on purpose -
            re-cutting the history to a new shape would make a change on the map look like a change on
            the ground - but a new baseline is needed before the new boundary is watched.
          </span>
        </p>
      )}

      <PlotMap plotCode={data.plot_code} readOnly value={data.watched.geometry} />

      <dl className="sat-counts">
        <div><dt>Watching</dt><dd>Version {watched.version_no}{data.watching_current ? ' (current)' : ''}</dd></div>
        <div><dt>Mapped area</dt><dd>{formatNumber(watched.area_ha, 3)} ha</dd></div>
        <div><dt>How it was captured</dt><dd>{watched.capture_method ?? '--'}
          {watched.accuracy_meters ? ` (±${formatNumber(watched.accuracy_meters, 0)} m)` : ''}</dd></div>
        <div><dt>Approved</dt><dd>{watched.approval_status}</dd></div>
        <div><dt>Captured</dt><dd>{watched.captured_on ? formatDate(watched.captured_on) : '--'}</dd></div>
        <div><dt>Plot</dt><dd><MapPin size={12} /> {watched.plot_code} - {watched.farmer_name ?? '--'}</dd></div>
      </dl>
    </div>
  );
}
